// Allow-list of product events and the properties each may carry. The backend
// holds the same catalogue (feedbacksBackend AppEventCatalog) and drops anything
// that is not on it, so a change here needs the matching change there.
//
// Values are ids, enums, bounded integers and booleans only: never a name, an
// email, free text, a URL or a token. See docs/product-analytics-events.md.

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isId = (value) =>
  typeof value === 'string' && (OBJECT_ID.test(value) || UUID.test(value));

export const isUuid = (value) => typeof value === 'string' && UUID.test(value);

// Hash routes the app serves (App.jsx), most specific first. A path that
// matches none is reported as 'other', so ids and tokens never leave the page.
// The backend accepts exactly these (AppEventRoutes); keep the two in step.
export const ROUTE_PATTERNS = [
  '/teacher-onboarding',
  '/student-onboarding',
  '/non-school-teacher-onboarding',
  '/docs',
  '/getFeedback',
  '/documents/:id',
  '/drafts/:assignmentId',
  '/documentsReview/:id',
  '/feedbackRequest/documentsReview/:id',
  '/giveFeedback',
  '/completed',
  '/feedbackHistory/documentsReview/:id',
  '/feedbackHistory',
  '/class-insights',
  '/classes-insights',
  '/classes/:classId',
  '/classes',
  '/tasks/new',
  '/tasks/:assignmentId/start',
  '/tasks/:assignmentId/upload/:studentId',
  '/tasks/:assignmentId/edit',
  '/tasks/:assignmentId',
  '/tasks',
  '/sharedresponses',
  '/sharedtasks',
  '/sharedmarkingtools',
  '/advanced-marking',
  '/tools/advanced-marking',
  '/tools/advanced-controls',
  '/jeddai',
  '/tools/markingcriterias',
  '/tools/rubrics/:markingCriteriaId',
  '/tools/rubrics',
  '/tools/commentbanks/new',
  '/tools/commentbanks/:commentBankId',
  '/tools/commentbanks',
  '/tools/successcriterias/new',
  '/tools/successcriterias/:id',
  '/tools/successcriterias',
  '/tools/shortanswers/new',
  '/tools/shortanswers/:id',
  '/tools/shortanswers',
  '/tools',
  '/submissions/:id/review',
  '/submissions/:id/upload-response',
  '/submissions/:id/revise',
  '/submissions/:id',
  '/onboarding/marking',
  '/onboarding/tools/commentbanks',
  '/onboarding/tools/successcriterias',
  '/onboarding/tools/shortanswers',
  '/onboarding/new-task',
  '/onboarding/documents/:id',
  '/onboarding/submissions/:id',
  '/m/home',
  '/m/library',
  '/m/more',
  '/m/new-draft',
  '/m/notifications',
  '/m/samples/:key',
  '/m/samples',
  '/404',
  '/',
];

const KNOWN_ROUTES = new Set(ROUTE_PATTERNS);

/** One of the app's route templates, or 'other'. An exact lookup, no pattern. */
export const isRouteTemplate = (value) =>
  value === 'other' || (typeof value === 'string' && KNOWN_ROUTES.has(value));

const id = () => isId;
const bool = () => (value) => typeof value === 'boolean';
const int = (min, max) => (value) =>
  Number.isInteger(value) && value >= min && value <= max;
const oneOf =
  (...values) =>
  (value) =>
    values.includes(value);
const route = () => isRouteTemplate;

const KIND = oneOf('my_draft', 'task');
const PAYWALL_TRIGGER = oneOf(
  'credits_pill',
  'submit_gate',
  'credit_shortfall',
  'question_limit',
  'view_mark',
  'locked_feature',
  'advanced_marking',
  'bulk_upload',
  'onboarding_plans',
  'mobile',
  'other'
);
const AUDIENCE = oneOf('teacher', 'student');
const TOUR = oneOf('marking', 'draft', 'new_task', 'feedback', 'wizard');
const PLAN_SOURCE = oneOf(
  'upgrade_popup',
  'locked_feature',
  'mobile_account',
  'other'
);

export const CATALOGUE = {
  app_opened: { entry: oneOf('sign_in', 'returning') },
  page_viewed: { from_route: route() },
  page_engaged: {
    engaged_ms: int(0, 1_800_000),
    ended_by: oneOf('route_change', 'hidden'),
  },
  error_screen_shown: {
    kind: oneOf('not_found', 'forbidden', 'lms_relaunch', 'crash'),
  },
  draft_started: {
    draft_id: id(),
    from: oneOf('new', 'limit_reached', 'bulk', 'mobile'),
  },
  draft_opened: { draft_id: id(), kind: KIND, has_feedback: bool() },
  draft_sent: {
    draft_id: id(),
    kind: KIND,
    resubmit: bool(),
    rubric: bool(),
    success_criteria: bool(),
    comment_bank: bool(),
    short_answer: bool(),
    tools_from: oneOf('auto', 'manual', 'mixed', 'none'),
  },
  jeddai_feedback_revealed: {
    draft_id: id(),
    wait_ms: int(0, 3_600_000),
    seen_live: bool(),
  },
  feedback_viewed: {
    submission_id: id(),
    kind: KIND,
    feedback_by: oneOf(
      'jeddai_instant',
      'jeddai_assisted',
      'teacher',
      'unknown'
    ),
    status: oneOf('draft', 'submitted', 'reviewed', 'closed', 'other'),
  },
  feedback_comment_opened: {
    submission_id: id(),
    author: oneOf('jeddai', 'teacher', 'unknown'),
  },
  rubric_viewed: {
    marking_tool_id: id(),
    context: oneOf('feedback', 'task_setup', 'bulk_upload', 'tools'),
  },
  marking_tool_picked: {
    tool_type: oneOf(
      'rubric',
      'success_criteria',
      'comment_bank',
      'short_answer'
    ),
    marking_tool_id: id(),
    context: oneOf('draft', 'task', 'bulk_upload'),
    action: oneOf('picked', 'cleared'),
  },
  task_setup_opened: { task_id: id(), is_new: bool() },
  bulk_upload_step: {
    step: oneOf(
      'opened',
      'settings',
      'question',
      'files',
      'assign',
      'converting',
      'review',
      'marking',
      'submitted',
      'done',
      'exited'
    ),
    assignment_id: id(),
    file_count: int(0, 1000),
    student_count: int(0, 1000),
  },
  paywall_shown: { trigger: PAYWALL_TRIGGER, audience: AUDIENCE },
  paywall_clicked: {
    trigger: PAYWALL_TRIGGER,
    audience: AUDIENCE,
    action: oneOf(
      'plan_selected',
      'school_plan',
      'coupon',
      'continue_free',
      'dismissed'
    ),
    plan_type: oneOf('pro', 'school', 'student_plus', 'student_pro'),
    billing_cycle: oneOf('monthly', 'annually'),
  },
  tour_step_viewed: { tour: TOUR, step_index: int(0, 50) },
  tour_ended: {
    tour: TOUR,
    outcome: oneOf('completed', 'skipped', 'left'),
    step_index: int(0, 50),
  },
  school_plan_request_opened: { source: PLAN_SOURCE },
  school_plan_request_submitted: { source: PLAN_SOURCE },
};

/**
 * Returns only the properties the catalogue allows for `name`, with valid
 * values; everything else is dropped. Returns null for an unknown event.
 */
export const validateProps = (name, props) => {
  const spec = Object.prototype.hasOwnProperty.call(CATALOGUE, name)
    ? CATALOGUE[name]
    : null;
  if (!spec) return null;
  const clean = {};
  if (!props || typeof props !== 'object') return clean;
  Object.keys(spec).forEach((key) => {
    if (
      Object.prototype.hasOwnProperty.call(props, key) &&
      spec[key](props[key])
    ) {
      clean[key] = props[key];
    }
  });
  return clean;
};
